import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  archiveBidResponseDocument,
  getAllArchivedBidResponseDocuments,
  restoreBidResponseDocument,
  updateBidResponseDocument,
} from '../../../../../shared/api/magellan/bidResponseDocuments';
import type { UpdateBidResponseDocumentRequestDTO } from '../../../../../shared/api/magellan/bidResponseDocuments/dto';
import { getBidResponseById } from '../../../../../shared/api/magellan/bidResponses';
import type { BidResponse } from '../../../../../shared/entities/BidResponse';

export function useUpdateBidResponseDocumentMutation(bidResponseId: number) {
  const queryClient = useQueryClient();

  const getBidResponseByIdQueryKey = [getBidResponseById.name, bidResponseId];

  return useMutation({
    mutationFn: ({
      bidResponseId,
      bidResponseDocumentId,
      payload,
    }: {
      bidResponseId: number;
      bidResponseDocumentId: number;
      payload: UpdateBidResponseDocumentRequestDTO;
    }) =>
      updateBidResponseDocument({
        bidResponseId,
        bidResponseDocumentId,
        payload,
      }),
    onMutate: async ({ bidResponseDocumentId, payload }) => {
      await queryClient.cancelQueries({
        queryKey: getBidResponseByIdQueryKey,
      });

      const oldData = queryClient.getQueryData<BidResponse>(
        getBidResponseByIdQueryKey,
      );

      if (!oldData) {
        return false;
      }

      const newData: BidResponse = {
        ...oldData,
        documents: oldData.documents?.map(document =>
          document.id === bidResponseDocumentId
            ? { ...document, ...payload }
            : document,
        ),
      };

      queryClient.setQueryData(getBidResponseByIdQueryKey, newData);

      return { oldData };
    },
    onError: (_error, _variables, context) => {
      if (!context) {
        return;
      }

      queryClient.setQueryData(getBidResponseByIdQueryKey, context.oldData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getBidResponseByIdQueryKey });
    },
  });
}

export function useFindAllArchivedBidResponseDocumentsQuery(
  bidResponseId: number,
) {
  return useQuery({
    queryKey: [getAllArchivedBidResponseDocuments.name, bidResponseId],
    queryFn: () => getAllArchivedBidResponseDocuments(bidResponseId),
  });
}

export function useArchiveBidResponseDocumentMutation(bidResponseId: number) {
  const queryClient = useQueryClient();
  const getBidResponseByIdQueryKey = [getBidResponseById.name, bidResponseId];

  return useMutation({
    mutationFn: (bidResponseDocumentId: number) =>
      archiveBidResponseDocument({
        bidResponseId,
        bidResponseDocumentId,
      }),
    onMutate: async bidResponseDocumentId => {
      await queryClient.cancelQueries({
        queryKey: getBidResponseByIdQueryKey,
      });

      const oldData = queryClient.getQueryData<BidResponse>(
        getBidResponseByIdQueryKey,
      );

      if (!oldData) {
        return false;
      }

      const newData: BidResponse = {
        ...oldData,
        documents: oldData.documents?.filter(
          document => document.id !== bidResponseDocumentId,
        ),
      };

      queryClient.setQueryData(getBidResponseByIdQueryKey, newData);

      return { oldData };
    },
    onError: (_error, _variables, context) => {
      if (!context) {
        return;
      }

      queryClient.setQueryData(getBidResponseByIdQueryKey, context.oldData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getBidResponseByIdQueryKey });
    },
  });
}

export function useRestoreBidResponseDocumentMutation(bidResponseId: number) {
  const queryClient = useQueryClient();
  const getArchivedBidResponseDocumentsQueryKey = [
    getAllArchivedBidResponseDocuments.name,
    bidResponseId,
  ];

  return useMutation({
    mutationFn: (bidResponseDocumentId: number) =>
      restoreBidResponseDocument({ bidResponseDocumentId, bidResponseId }),
    onMutate: async bidResponseDocumentId => {
      await queryClient.cancelQueries({
        queryKey: getArchivedBidResponseDocumentsQueryKey,
      });

      const oldData = queryClient.getQueryData<BidResponse>(
        getArchivedBidResponseDocumentsQueryKey,
      );

      if (!oldData) {
        return false;
      }

      const newData: BidResponse = {
        ...oldData,
        documents: oldData.documents?.filter(
          document => document.id !== bidResponseDocumentId,
        ),
      };

      queryClient.setQueryData(
        getArchivedBidResponseDocumentsQueryKey,
        newData,
      );

      return { oldData };
    },
    onError: (_error, _variables, context) => {
      if (!context) {
        return;
      }

      queryClient.setQueryData(
        getArchivedBidResponseDocumentsQueryKey,
        context.oldData,
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: getArchivedBidResponseDocumentsQueryKey,
      });
    },
  });
}
